import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Copy, Check, Plus, Trash2, Users, TrendingUp, Link as LinkIcon, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AffiliatesManager = () => {
  const { token } = useAuth();
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newAffiliateName, setNewAffiliateName] = useState('');
  const [copiedCode, setCopiedCode] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchAffiliates();
  }, []);

  const fetchAffiliates = async () => {
    try {
      const response = await fetch(`${API_URL}/api/affiliates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAffiliates(data);
      }
    } catch (error) {
      console.error('Error fetching affiliates:', error);
    } finally {
      setLoading(false);
    }
  };

  const createAffiliate = async () => {
    if (!newAffiliateName.trim()) {
      alert('Entrez un nom pour le streamer/affilié');
      return;
    }

    // Clean the name (lowercase, no spaces, no special chars)
    const cleanName = newAffiliateName.toLowerCase().replace(/[^a-z0-9]/g, '');
    
    try {
      const response = await fetch(`${API_URL}/api/affiliates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newAffiliateName.trim(),
          code: cleanName,
          type: 'streamer'
        })
      });

      if (response.ok) {
        setNewAffiliateName('');
        fetchAffiliates();
      } else {
        const error = await response.json();
        alert(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      console.error('Error creating affiliate:', error);
      alert('Une erreur est survenue');
    }
  };

  const deleteAffiliate = async (code) => {
    if (!confirm(`Supprimer l'affilié "${code}" ?`)) return;

    try {
      const response = await fetch(`${API_URL}/api/affiliates/${code}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchAffiliates();
      }
    } catch (error) {
      console.error('Error deleting affiliate:', error);
    }
  };

  const copyLink = (code) => {
    const link = `${window.location.origin}/${code}`;
    navigator.clipboard.writeText(link);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  // Filter affiliates based on search query
  const filteredAffiliates = affiliates.filter(aff => 
    aff.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    aff.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return <div className="text-center py-12 text-gray-400">Chargement...</div>;
  }

  const totalClicks = affiliates.reduce((sum, aff) => sum + (aff.clicks || 0), 0);
  const totalOrders = affiliates.reduce((sum, aff) => sum + (aff.orders || 0), 0);

  return (
    <div className="min-h-screen bg-[#0a0b14] py-20 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">
            Gestion des <span className="text-cyan-500">Affiliés</span>
          </h1>
          <p className="text-gray-400">
            Créez des liens personnalisés pour vos streamers et partenaires
          </p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-cyan-900/20 to-cyan-950/20 border-cyan-800/30">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-cyan-600/20 flex items-center justify-center">
                  <Users className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Total Affiliés</p>
                  <p className="text-3xl font-bold text-white">{affiliates.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-900/20 to-orange-950/20 border-orange-800/30">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-orange-600/20 flex items-center justify-center">
                  <LinkIcon className="w-6 h-6 text-orange-400" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Total Clics</p>
                  <p className="text-3xl font-bold text-white">{totalClicks}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-900/20 to-green-950/20 border-green-800/30">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-green-600/20 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Total Commandes</p>
                  <p className="text-3xl font-bold text-white">{totalOrders}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Create New Affiliate */}
        <Card className="bg-[#0f1119] border-cyan-900/30 mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Plus className="w-5 h-5 text-cyan-400" />
              Créer un nouveau lien d'affiliation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <Input
                placeholder="Nom du streamer (ex: Kamet0, Squeezie, Solary...)"
                value={newAffiliateName}
                onChange={(e) => setNewAffiliateName(e.target.value)}
                className="bg-[#1a1b2e] border-cyan-900/30 text-white flex-1"
                onKeyPress={(e) => e.key === 'Enter' && createAffiliate()}
              />
              <Button 
                onClick={createAffiliate}
                className="bg-cyan-600 hover:bg-cyan-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Créer
              </Button>
            </div>
            <p className="text-gray-500 text-sm mt-2">
              Le code sera automatiquement généré (ex: "Kamet0" → code "kamet0")
            </p>
          </CardContent>
        </Card>

        {/* Affiliates List */}
        <Card className="bg-[#0f1119] border-cyan-900/30">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white">Liste des Affiliés</CardTitle>
              <Input
                placeholder="🔍 Rechercher un affilié..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-[#1a1b2e] border-cyan-900/30 text-white max-w-xs"
              />
            </div>
          </CardHeader>
          <CardContent>
            {filteredAffiliates.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                {searchQuery ? 'Aucun affilié trouvé pour cette recherche' : 'Aucun affilié créé. Commencez par en ajouter un ci-dessus!'}
              </div>
            ) : (
              <div className="space-y-4">
                {filteredAffiliates.map((affiliate) => (
                  <div
                    key={affiliate.code}
                    className="bg-[#1a1b2e] border border-cyan-900/20 rounded-lg p-4 hover:border-cyan-700/40 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="text-white font-semibold text-lg mb-1">
                          {affiliate.name}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-gray-400 mb-3">
                          <span className="px-2 py-1 bg-cyan-950/50 rounded text-cyan-400 font-mono">
                            {affiliate.code}
                          </span>
                          <span>•</span>
                          <span>{affiliate.clicks || 0} clics</span>
                          <span>•</span>
                          <span>{affiliate.orders || 0} commandes</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-[#0a0b14] px-3 py-2 rounded border border-cyan-900/20 text-gray-400 text-sm font-mono truncate">
                            {window.location.origin}/{affiliate.code}
                          </div>
                          <Button
                            size="sm"
                            onClick={() => copyLink(affiliate.code)}
                            className="bg-cyan-700 hover:bg-cyan-600 text-white"
                          >
                            {copiedCode === affiliate.code ? (
                              <>
                                <Check className="w-4 h-4 mr-1" />
                                Copié
                              </>
                            ) : (
                              <>
                                <Copy className="w-4 h-4 mr-1" />
                                Copier
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => window.open(`${window.location.origin}/${affiliate.code}`, '_blank')}
                            className="text-gray-400 hover:text-white"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteAffiliate(affiliate.code)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-950/20"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AffiliatesManager;
